import { formatLarge } from '@benzinga/utils';
import { ColumnDef } from '@benzinga/table';

import { WatchlistFinancials } from '../../../data';
import { SymbolCell, RESEARCH_SYMBOL_COLUMN_WIDTH, formatCurrency, formatPercentage, ResearchCell } from '../utils';

export const FundamentalsColumnsDef: ColumnDef<WatchlistFinancials>[] = [
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: RESEARCH_SYMBOL_COLUMN_WIDTH,
    sortEnabled: true,
    cellRenderer: ({ data }) => <SymbolCell record={data} />,
    valueGetter: ({ data }) => data.symbol || '',
  },
  {
    field: 'value__market_cap',
    headerName: 'Market Cap',
    sortEnabled: true,
    valueGetter: ({ data }) => data.quote?.marketCap ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-14">
        {formatLarge(value)}
      </ResearchCell>
    ),
  },
  {
    field: 'value__ev',
    headerName: 'Enterprise Value',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.shareClassProfile?.enterpriseValue ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-16">
        {formatLarge(value)}
      </ResearchCell>
    ),
  },
  {
    field: 'value__p_e_ttm',
    headerName: 'PE (TTM)',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.valuationRatios?.[0]?.peRatio ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-10">
        {value.toFixed(2)}
      </ResearchCell>
    ),
  },
  {
    field: 'value__p_e_fwd',
    headerName: 'PE (FWD)',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.valuationRatios?.[0]?.forwardPeRatio ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-10">
        {value.toFixed(2)}
      </ResearchCell>
    ),
  },
  {
    field: 'value__peg_ttm',
    headerName: 'PEG',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.valuationRatios?.[0]?.pegRatio ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-10">
        {value.toFixed(2)}
      </ResearchCell>
    ),
  },
  {
    field: 'value__p_s_ttm',
    headerName: 'PS',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.valuationRatios?.[0]?.psRatio ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-10">
        {value.toFixed(2)}
      </ResearchCell>
    ),
  },
  {
    field: 'value__p_b_ttm',
    headerName: 'PB',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.valuationRatios?.[0]?.pbRatio ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-10">
        {value.toFixed(2)}
      </ResearchCell>
    ),
  },
  {
    field: 'value__ev_ebitda',
    headerName: 'EV/EBITDA',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.valuationRatios?.[0]?.evToEbitda ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-12">
        {value.toFixed(2)}
      </ResearchCell>
    ),
  },
  {
    field: 'value__book_value_share',
    headerName: 'Book Value/Share',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.valuationRatios?.[0]?.bookValuePerShare ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-14">
        {formatCurrency(value)}
      </ResearchCell>
    ),
  },
  {
    field: 'ownership__insider_perc',
    headerName: 'Insider %',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.ownership?.[0]?.insiderPercentOwned ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-12">
        {formatPercentage(value)}
      </ResearchCell>
    ),
  },
  {
    field: 'ownership__institutional_percent',
    headerName: 'Institutional %',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.ownership?.[0]?.institutionPercentHeld ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-12">
        {formatPercentage(value)}
      </ResearchCell>
    ),
  },
  {
    field: 'debt__total_debt',
    headerName: 'Total Debt',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.financials?.[0]?.balanceSheet?.totalDebt ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-16">
        {formatLarge(value)}
      </ResearchCell>
    ),
  },
  {
    field: 'debt__lt_debt',
    headerName: 'LT Debt',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.financials?.[0]?.balanceSheet?.longTermDebt ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-16">
        {formatLarge(value)}
      </ResearchCell>
    ),
  },
  {
    field: 'debt__total_cash',
    headerName: 'Total Cash',
    sortEnabled: true,
    valueGetter: ({ data }) => data.financials?.financials?.[0]?.balanceSheet?.cashAndCashEquivalents ?? 0,
    cellRenderer: ({ value }) => (
      <ResearchCell hasValue={Boolean(value)} skeletonClassName="h-4 w-16">
        {formatLarge(value)}
      </ResearchCell>
    ),
  },
];
